import React, { useEffect, useState } from "react";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, IconButton } from "@mui/joy";
import { Autocomplete, Switch, TextField } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ProjectEditModal from "./projecteditmodal";

// 프로젝트/섹션 관리 탭 (본사직원페이지, 시설 관리 공용)
// props: projects(트리), letsUpdate, category(선택 - "headquarters" 기본 | "facility")
const ManageProjects: React.FC<any> = (props) => {

    const [selectedProjectV2Id, setSelectedProjectV2Id] = useState<string>("");
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalMode, setModalMode] = useState<string>("project"); // "project" | "section"
    const [editTarget, setEditTarget] = useState<any>(null);
    //'메인에 표시' 연결 직원 후보 (todoistApiKey 있는 sunbae 직원) — 본사직원 페이지에서만 사용
    const [managers, setManagers] = useState<any[]>([]);
    const [homeSavingV2Id, setHomeSavingV2Id] = useState<string>("");

    const projects = props.projects || [];

    //'메인에 표시' 스위치는 본사직원 페이지(headquarters)에만 표시
    const showHomeControls = props.category !== "facility";

    useEffect(() => {

        if (!showHomeControls) return;

        const getManagers = async () => {
            try {
                const result: any = await getFetch("/dashboard/todoist/managerCandidates");
                if (result && result.message === "success") {
                    setManagers([...result.data]);
                }
            } catch (e) {
                console.log(e);
            }
        };

        getManagers();

    }, [showHomeControls]);

    const managerOptions = managers.map((m: any) => ({ id: m.id, name: m.name, label: m.name }));

    const selectedProject = projects.find((p: any) => p.v2_id === selectedProjectV2Id);

    // 선택 프로젝트의 실제 섹션 (가상 "미배정" 제외)
    const sections = selectedProject
        ? (selectedProject.sections || []).filter((s: any) => !String(s.v2_id).endsWith("sectionwithout"))
        : [];

    const toastSuccess = () => {
        toast.success("저장되었습니다.", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "colored"
        });
    };

    //'메인에 표시' 스위치/연결 직원 저장 (즉시 저장, 직원 1명 ↔ 프로젝트 1개는 서버가 검사)
    const saveProjectHome = async (project: any, showOnHome: number, homeUserId: number | null) => {
        try {
            setHomeSavingV2Id(project.v2_id);
            const result: any = await postFetch("/dashboard/todoist/setProjectHome", {
                projectV2Id: project.v2_id,
                showOnHome,
                homeUserId
            });

            if (result && result.message === "success") {
                toastSuccess();
            } else if (result && result.message === "ALREADY_LINKED") {
                toast.error(`이미 '${result.projectName}' 프로젝트에 연결된 직원입니다.`, {
                    position: "bottom-right",
                    autoClose: 3500,
                    hideProgressBar: true,
                    theme: "colored"
                });
            } else {
                toast.error("저장에 실패했습니다.", {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
            }
        } catch (e) {
            console.log(e);
        } finally {
            setHomeSavingV2Id("");
            //성공/실패 모두 서버 값으로 화면 복원
            props.letsUpdate();
        }
    };

    // 프로젝트 순서 변경 (인접 항목 스왑 후 전체 순서 전송)
    const moveProject = async (index: number, direction: number) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= projects.length) return;

        const reordered = [...projects];
        const temp = reordered[index];
        reordered[index] = reordered[newIndex];
        reordered[newIndex] = temp;

        const items = reordered.map((p: any, i: number) => ({ v2_id: p.v2_id, order: i + 1 }));

        try {
            await postFetch("/dashboard/todoist/reorderProject", { items });
            props.letsUpdate();
        } catch (e) {
            console.log(e);
        }
    };

    // 섹션 순서 변경
    const moveSection = async (index: number, direction: number) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= sections.length) return;

        const reordered = [...sections];
        const temp = reordered[index];
        reordered[index] = reordered[newIndex];
        reordered[newIndex] = temp;

        const items = reordered.map((s: any, i: number) => ({ v2_id: s.v2_id, order: i + 1 }));

        try {
            await postFetch("/dashboard/todoist/reorderSection", { projectV2Id: selectedProjectV2Id, items });
            props.letsUpdate();
        } catch (e) {
            console.log(e);
        }
    };

    const deleteProject = async (project: any) => {
        if (!window.confirm(`'${project.name}' 프로젝트를 삭제할까요? (할일은 보존됩니다)`)) return;
        try {
            await postFetch("/dashboard/todoist/deleteProject", { projectV2Id: project.v2_id });
            toastSuccess();
            if (selectedProjectV2Id === project.v2_id) setSelectedProjectV2Id("");
            props.letsUpdate();
        } catch (e) {
            console.log(e);
        }
    };

    const deleteSection = async (section: any) => {
        if (!window.confirm(`'${section.name}' 섹션을 삭제할까요? (할일은 미배정으로 이동)`)) return;
        try {
            await postFetch("/dashboard/todoist/deleteSection", { sectionV2Id: section.v2_id });
            toastSuccess();
            props.letsUpdate();
        } catch (e) {
            console.log(e);
        }
    };

    const openCreateProject = () => { setModalMode("project"); setEditTarget(null); setModalOpen(true); };
    const openRenameProject = (project: any) => { setModalMode("project"); setEditTarget(project); setModalOpen(true); };
    const openCreateSection = () => {
        if (!selectedProjectV2Id) { alert("먼저 프로젝트를 선택하세요."); return; }
        setModalMode("section"); setEditTarget(null); setModalOpen(true);
    };
    const openRenameSection = (section: any) => { setModalMode("section"); setEditTarget(section); setModalOpen(true); };

    const rowStyle: any = {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 12px", marginBottom: "6px", borderRadius: "8px",
        backgroundColor: "#fff"
    };

    return (
        <div style={{ display: "flex", gap: "32px", padding: "24px 8px", maxWidth: showHomeControls ? "1200px" : "900px" }}>

            {/* 프로젝트 목록 */}
            <div style={{ flex: showHomeControls ? 1.6 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600 }}>프로젝트</div>
                    <Button size="sm" variant="solid" onClick={openCreateProject}>+ 프로젝트 추가</Button>
                </div>
                {
                    projects.map((project: any, index: number) => (
                        <div key={project.v2_id}
                            style={{
                                ...rowStyle,
                                border: project.v2_id === selectedProjectV2Id ? "2px solid #3d50b0" : "1px solid #e0e0e0",
                                cursor: "pointer"
                            }}
                            onClick={() => setSelectedProjectV2Id(project.v2_id)}
                        >
                            <span style={{ fontSize: "15px", flexShrink: 0 }}>{project.name}</span>
                            {
                                showHomeControls &&
                                <span
                                    style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto", marginRight: "8px" }}
                                    onClick={(e: any) => e.stopPropagation()}
                                >
                                    <span style={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>메인에 표시</span>
                                    <Switch
                                        size="small"
                                        checked={!!project.showOnHome}
                                        disabled={homeSavingV2Id === project.v2_id}
                                        onChange={(e: any) => saveProjectHome(project, e.target.checked ? 1 : 0, e.target.checked ? (project.homeUserId ?? null) : null)}
                                    />
                                    {
                                        !!project.showOnHome &&
                                        <Autocomplete
                                            options={managerOptions}
                                            value={managerOptions.find((o: any) => o.id === project.homeUserId) || null}
                                            onChange={(_: any, newValue: any) => saveProjectHome(project, 1, newValue ? newValue.id : null)}
                                            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                            disabled={homeSavingV2Id === project.v2_id}
                                            size="small"
                                            sx={{ width: "160px" }}
                                            noOptionsText="검색 결과 없음"
                                            renderInput={(params: any) =>
                                                <TextField
                                                    {...params}
                                                    placeholder="이름 검색"
                                                    color={project.homeUserId ? "primary" : "warning"}
                                                    helperText={project.homeUserId ? "" : "직원 미연결"}
                                                    focused={!project.homeUserId}
                                                />
                                            }
                                        />
                                    }
                                </span>
                            }
                            <span style={{ display: "flex", gap: "2px", flexShrink: 0 }} onClick={(e: any) => e.stopPropagation()}>
                                <IconButton size="sm" variant="plain" onClick={() => moveProject(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                <IconButton size="sm" variant="plain" onClick={() => moveProject(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                <IconButton size="sm" variant="plain" onClick={() => openRenameProject(project)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="sm" variant="plain" color="danger" onClick={() => deleteProject(project)}><DeleteIcon fontSize="small" /></IconButton>
                            </span>
                        </div>
                    ))
                }
            </div>

            {/* 섹션 목록 */}
            <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600 }}>
                        섹션 {selectedProject ? `(${selectedProject.name})` : ""}
                    </div>
                    <Button size="sm" variant="solid" onClick={openCreateSection} disabled={!selectedProjectV2Id}>+ 섹션 추가</Button>
                </div>
                {
                    !selectedProjectV2Id
                        ? <div style={{ color: "#888", fontSize: "14px" }}>프로젝트를 선택하세요.</div>
                        : sections.length === 0
                            ? <div style={{ color: "#888", fontSize: "14px" }}>섹션이 없습니다.</div>
                            : sections.map((section: any, index: number) => (
                                <div key={section.v2_id} style={{ ...rowStyle, border: "1px solid #e0e0e0" }}>
                                    <span style={{ fontSize: "15px" }}>{section.name}</span>
                                    <span style={{ display: "flex", gap: "2px" }}>
                                        <IconButton size="sm" variant="plain" onClick={() => moveSection(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                        <IconButton size="sm" variant="plain" onClick={() => moveSection(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                        <IconButton size="sm" variant="plain" onClick={() => openRenameSection(section)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="sm" variant="plain" color="danger" onClick={() => deleteSection(section)}><DeleteIcon fontSize="small" /></IconButton>
                                    </span>
                                </div>
                            ))
                }
            </div>

            <ProjectEditModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={() => props.letsUpdate()}
                mode={modalMode}
                editTarget={editTarget}
                projectV2Id={selectedProjectV2Id}
                category={props.category}
            />

        </div>
    );
};

export default ManageProjects;
